<button
  [matTooltip]="'Notifications' | transloco"
  mat-icon-button
  (click)="openPanel()"
  #notificationsOrigin
>
  <ng-container *ngIf="metaData?.unread_notifications_count !== 0">
    <span class="absolute top-0 right-0 left-0 flex items-center justify-center h-3">
      <span
        class="flex items-center justify-center flex-shrink-0 min-w-4 h-4 px-1 ms-4 mt-2.5 rounded-full bg-warn-700 text-white text-xs font-medium"
      >
        {{ metaData?.unread_notifications_count }}
      </span>
    </span>
  </ng-container>
  <mat-icon [svgIcon]="'heroicons_outline:bell'"></mat-icon>
</button>

<!-- Notifications panel -->
<ng-template #notificationsPanel>
  <div
    class="max-h-200 fixed inset-0 sm:static sm:inset-auto flex flex-col sm:min-w-90 sm:w-90 sm:rounded-2xl overflow-hidden shadow-lg"
  >
    <!-- Header -->
    <div class="flex flex-shrink-0 items-center py-4 pe-4 ps-6 bg-primary text-on-primary">
      <div class="sm:hidden -ms-1 me-3">
        <button mat-icon-button (click)="closePanel()">
          <mat-icon class="icon-size-5 text-current" [svgIcon]="'heroicons_solid:x'"></mat-icon>
        </button>
      </div>
      <div class="text-lg font-medium leading-10">
        {{ 'notifications' | transloco }}
      </div>
      <div class="ms-auto">
        <button
          mat-icon-button
          class="bg-primary-800 text-white"
          [matTooltip]="'View All Notifications' | transloco"
          (click)="markAllAsRead(); closePanel()"
        >
          <mat-icon
            class="icon-size-5 text-current"
            [svgIcon]="'heroicons_outline:clipboard-list'"
          ></mat-icon>
        </button>
      </div>
    </div>
    <!-- Content -->
    <div
      (scrolled)="onScroll()"
      [infiniteScrollDistance]="3"
      [scrollWindow]="false"
      infiniteScroll
      class="relative flex flex-col flex-auto sm:max-h-120 divide-y overflow-y-auto bg-card"
    >
      <!-- Notifications -->
      <ng-container *ngFor="let notification of notifications">
        <div
          class="flex group hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
          [ngClass]="{ unread: !notification.is_read }"
        >
          <!-- Notification with a link -->
          <ng-container>
            <a
              class="flex flex-auto py-5 ps-6 cursor-pointer"
              [ngStyle]="{
                direction: transloco.getActiveLang() === 'ar' ? 'rtl' : 'ltr'
              }"
              (click)="makeItRead(notification)"
            >
              <ng-container *ngTemplateOutlet="notificationContent"></ng-container>
            </a>
          </ng-container>
        </div>

        <!-- Notification content template -->
        <ng-template #notificationContent>
          <!-- Title, description & time -->
          <div class="flex flex-col flex-auto ms-2">
            <div class="flex flex-row items-center gap-2 font-semibold">
              <!-- Icon -->
              <app-user-avatar
                *ngIf="notification.data.causer_email"
                [name]="notification.data.causer_name"
                [email]="notification.data.causer_email"
              ></app-user-avatar>
              <!--For old notifications that doesn't have causers-->

              <mat-icon
                *ngIf="!notification.data.causer_email"
                class="icon-size-8"
                [svgIcon]="'mat_solid:circle_notifications'"
              >
              </mat-icon>
              <!-- Notification text -->
              <span *ngIf="notification.data.text"
                >{{ notification.data.text
                }}<span class="text-secondary text-sm ms-2 whitespace-nowrap">{{
                  notification.created_at | timeAgo
                }}</span></span
              >
              <div class="relative flex flex-col me-2">
                <!-- Indicator -->
                <button
                  class="w-6 h-6 min-h-6"
                  mat-icon-button
                  [matTooltip]="notification.is_read ? ('Read' | transloco) : ('New' | transloco)"
                >
                  <span
                    class="w-2 h-2 rounded-full"
                    [ngClass]="{
                      'bg-gray-400 dark:bg-gray-500 sm:opacity-0 sm:group-hover:opacity-100':
                        notification.is_read,
                      'bg-primary': !notification.is_read
                    }"
                  ></span>
                </button>
              </div>
            </div>
            <div class="flex flex-col justify-center py-2 gap-2" style="padding-inline-start: 37px">
              <div
                class="flex flex-row items-center gap-2"
                *ngIf="notification.data.observation_title"
              >
                <div>
                  <mat-icon
                    class="icon-size-4"
                    style="padding-top: 2px"
                    svgIcon="heroicons_outline:document-text"
                  ></mat-icon>
                </div>
                <div style="max-width: 220px" [matTooltip]="'Observation Title' | transloco">
                  {{ notification.data.observation_title | titlecase }}
                </div>
              </div>

              <div class="flex flex-row items-center gap-2 text-xs text-secondary">
                <div
                  [matTooltip]="'unique_id' | transloco"
                  *ngIf="notification.data.observation_unique_id"
                >
                  {{ notification.data.observation_unique_id }}
                </div>
                <span
                  class="w-1 h-1 rounded-full bg-accent-500"
                  *ngIf="notification.data.observation_unique_id"
                ></span>
                <div
                  [matTooltip]="'Project Name' | transloco"
                  *ngIf="notification.data.project_name"
                >
                  {{ notification.data.project_name }}
                </div>
              </div>
            </div>
          </div>
        </ng-template>
      </ng-container>

      <!-- No notifications -->
      <ng-container *ngIf="!notifications || !notifications.length">
        <div
          class="flex flex-col flex-auto items-center justify-center sm:justify-start py-12 px-8"
        >
          <div
            class="flex flex-0 items-center justify-center w-14 h-14 rounded-full bg-primary-100"
          >
            <mat-icon class="text-primary-500-700" [svgIcon]="'heroicons_outline:bell'"></mat-icon>
          </div>
          <div class="mt-5 text-2xl font-semibold tracking-tight">
            {{ 'no notifications' | transloco }}
          </div>
          <div class="w-full max-w-60 mt-1 text-md text-center text-secondary">
            {{ 'When you have notifications, they will appear here.' | transloco }}
          </div>
        </div>
      </ng-container>
    </div>
  </div>
</ng-template>
